import {
  BLOG_PAGE_SIZE,
  BlogPost,
  BlogPostAuthor,
  PaginatedBlogPosts,
} from '@/apiTypes/blog';
import {authMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import {createServerFn} from '@tanstack/react-start';
import {Prisma} from '@wayra/database';
import {z} from 'zod';

/** Prisma select derived from BlogPostAuthor schema — single source of truth. */
const blogAuthorSelect = Object.fromEntries(
  Object.keys(BlogPostAuthor.shape).map((key) => [key, true])
);

/** Prisma include for flattened tag data on blog posts. */
const blogTagsInclude = {
  tags: {
    include: {tag: {select: {id: true, name: true}}},
  },
};

/** Transforms Prisma blog post with nested join-table tags into flat tag array. */
function flattenTags<
  T extends {tags: Array<{tag: {id: string; name: string}}>},
>(post: T) {
  return {
    ...post,
    tags: post.tags.map((bt) => bt.tag),
  };
}

/**
 * Fetches all blog posts for the admin list view.
 * Superadmin only. Ordered by creation date descending.
 */
export const adminBlogPostsQuery = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .handler(async ({context}) => {
    const {profile} = context.auth;
    if (profile.level !== 'superadmin') {
      throw new Error('Only superadmins can view admin blog posts');
    }

    const db = await prismaClient();

    const posts = await db.blogPost.findMany({
      orderBy: {createdAt: 'desc'},
      include: {
        author: {
          select: blogAuthorSelect,
        },
        ...blogTagsInclude,
      },
    });

    return z.array(BlogPost).parse(posts.map(flattenTags));
  });

/**
 * Fetches a paginated list of blog posts for the public blog listing.
 * No authentication required. Returns posts without content.
 */
export const blogPostsQuery = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) =>
    z
      .object({
        page: z.number().int().positive(),
        tag: z.string().optional(),
      })
      .parse(data)
  )
  .handler(async ({data: {page, tag}}) => {
    const db = await prismaClient();

    const where: Prisma.BlogPostWhereInput = {status: 'PUBLISHED'};
    if (tag) {
      where.tags = {
        some: {tag: {name: {equals: tag, mode: 'insensitive'}}},
      };
    }

    const [posts, totalCount] = await Promise.all([
      db.blogPost.findMany({
        where,
        orderBy: {createdAt: 'desc'},
        skip: (page - 1) * BLOG_PAGE_SIZE,
        take: BLOG_PAGE_SIZE,
        select: {
          id: true,
          createdAt: true,
          updatedAt: true,
          title: true,
          slug: true,
          excerpt: true,
          heroImageUrl: true,
          status: true,
          publishedAt: true,
          authorId: true,
          author: {select: blogAuthorSelect},
          tags: {
            include: {tag: {select: {id: true, name: true}}},
          },
        },
      }),
      db.blogPost.count({where}),
    ]);

    return PaginatedBlogPosts.parse({
      posts: posts.map(flattenTags),
      totalCount,
      totalPages: totalCount === 0 ? 0 : Math.ceil(totalCount / BLOG_PAGE_SIZE),
      currentPage: page,
    });
  });

/**
 * Fetches a single blog post by slug for the public post detail page.
 * No authentication required. Returns full content or null if not found.
 */
export const blogPostBySlugQuery = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) => z.string().min(1).parse(data))
  .handler(async ({data: slug}) => {
    const db = await prismaClient();

    const post = await db.blogPost.findFirst({
      where: {slug, status: 'PUBLISHED'},
      include: {
        author: {select: blogAuthorSelect},
        ...blogTagsInclude,
      },
    });

    if (!post) return null;

    return BlogPost.parse(flattenTags(post));
  });

/**
 * Fetches a single blog post by slug for superadmin preview.
 * Returns posts regardless of status. Superadmin only.
 */
export const adminBlogPostBySlugQuery = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown) => z.string().min(1).parse(data))
  .handler(async ({data: slug, context}) => {
    const {profile} = context.auth;
    if (profile.level !== 'superadmin') {
      throw new Error('Only superadmins can preview blog posts');
    }

    const db = await prismaClient();

    const post = await db.blogPost.findFirst({
      where: {slug},
      include: {
        author: {select: blogAuthorSelect},
        ...blogTagsInclude,
      },
    });

    if (!post) return null;

    return BlogPost.parse(flattenTags(post));
  });

/**
 * Fetches a single blog post by ID for admin editing.
 * Superadmin only. Throws if the post is not found.
 */
export const adminBlogPostQuery = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown) => z.string().uuid().parse(data))
  .handler(async ({data: id, context}) => {
    const {profile} = context.auth;
    if (profile.level !== 'superadmin') {
      throw new Error('Only superadmins can view admin blog posts');
    }

    const db = await prismaClient();

    const post = await db.blogPost.findUnique({
      where: {id},
      include: {
        author: {select: blogAuthorSelect},
        ...blogTagsInclude,
      },
    });

    if (!post) {
      throw new Error('Blog post not found');
    }

    return BlogPost.parse(flattenTags(post));
  });
